import React, {forwardRef, useRef, useImperativeHandle} from "react";

const KEY_CODES = {
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    ENTER: 13,
};
export const TAB_GROUP_FOCUS_CLASS = "ch-tg-f";
const SELECTABLE_INTERNAL_COMPONENTS = "a, button, input, select, textarea, [role='button']";

const TabGroupWrap = forwardRef((props, ref) => {
    const {targetRef, rowSelector, cellSelector, disabled = false, onFocusCellChange, children} = props;
    const baseRef = useRef();
    const updatingRef = useRef(false);
    const currentPosDefault = {rowIndex: 0, colIndex: 0, subIndex: 0};
    const currentPosRef = useRef(currentPosDefault);
    const cleanupRef = useRef();

    useImperativeHandle(ref, () => ({
        build: () => {
            setupFocusManager();
        }
    }));

    const setupFocusManager = () => {
        if (cleanupRef.current) cleanupRef.current();

        const baseTarget = targetRef.current?.querySelector(".ant-table-content");
        if (!baseTarget) return;
        baseRef.current = baseTarget;

        currentPosRef.current = currentPosDefault;
        const matrix = getMatrixInfo();
        // wait a frame then focus the initial cell
        requestAnimationFrame(() => {
            // ensure focus happens after any pending render
            focusCell(matrix.currentCell, matrix, false);
        });

        const observer = new MutationObserver(() => {
            requestAnimationFrame(() => {
                const tabbableEls = baseRef.current?.querySelectorAll('[tabindex]:not([tabindex="-1"])');
                if (tabbableEls.length === 0 && !updatingRef.current) {
                    currentPosRef.current = currentPosDefault;
                    const matrix = getMatrixInfo();
                    focusCell(matrix.currentCell, matrix, false);
                }
            });
        });

        baseRef.current.addEventListener("click", handleClick);
        baseRef.current.addEventListener("keydown", handleKeyDown);
        observer.observe(baseTarget, { childList: true, subtree: true });

        cleanupRef.current = () => {
            baseRef.current?.removeEventListener("click", handleClick);
            baseRef.current?.removeEventListener("keydown", handleKeyDown);
            observer.disconnect();
        };
    }

    const clearTabIndex = () => {
        const focusedCells = baseRef.current?.querySelectorAll(`${cellSelector}[tabindex="0"], ${SELECTABLE_INTERNAL_COMPONENTS}`);
        if (!focusedCells || focusedCells.length === 0) return;

        focusedCells.forEach(cell => {
            if (cell.matches(SELECTABLE_INTERNAL_COMPONENTS) && cell.tabIndex !== -1) {
                cell.setAttribute("tabindex", "-1");
            } else if (cell.tabIndex !== -1) {
                cell.removeAttribute("tabindex");
                cell.removeAttribute("focusable");
            }
        });
    };

    const handleClick = event => {
        if (!event || disabled) return;
        if (event.target.matches(SELECTABLE_INTERNAL_COMPONENTS)) return;
        const newCell = getCellFromTarget(event.target);
        const matrix = getMatrixInfo();
        focusCell(newCell, matrix, true);
    }

    const handleKeyDown = event => {
        if (!event || disabled) return;

        let key = event.which || event.keyCode;
        let nextCell;
        const matrix = getMatrixInfo();

        switch (key) {
            case KEY_CODES.UP:
                nextCell = getNextVisibleCell(matrix, 0, -1);
                break;
            case KEY_CODES.DOWN:
                nextCell = getNextVisibleCell(matrix, 0, 1);
                break;
            case KEY_CODES.LEFT:
            case KEY_CODES.RIGHT:
                const xDir = key === KEY_CODES.RIGHT ? 1 : -1;
                const internalComponents = matrix.currentCell.querySelectorAll(SELECTABLE_INTERNAL_COMPONENTS);
                if (internalComponents.length > 1) {
                    const newSubIndex = (currentPosRef.current.subIndex || 0) + xDir;
                    const inBounds = newSubIndex >= 0 && newSubIndex < internalComponents.length;
                    if (inBounds) {
                        currentPosRef.current.subIndex = newSubIndex;
                        focusCell(matrix.currentCell, matrix, true);
                        event.preventDefault();
                        return;
                    }
                }
                nextCell = getNextVisibleCell(matrix, xDir, 0);
                break;
            case KEY_CODES.PAGE_DOWN:
                nextCell = getNextVisibleCell(matrix, 0, 5);
                break;
            case KEY_CODES.PAGE_UP:
                nextCell = getNextVisibleCell(matrix, 0, -5);
                break;
            case KEY_CODES.HOME:
                if (event.ctrlKey) {
                    nextCell = matrix.cells?.[0]?.[0];
                } else {
                    nextCell = matrix.cells?.[matrix.rowIndex]?.[0];
                }
                break;
            case KEY_CODES.END:
                if (event.ctrlKey) {
                    const lastRow = matrix.cells?.[matrix.cells.length - 1];
                    nextCell = lastRow?.[lastRow.length - 1];
                } else {
                    const row = matrix.cells?.[matrix.rowIndex];
                    nextCell = row?.[row.length - 1];
                }
                break;
            case KEY_CODES.ENTER:
                if (event.target.tagName === "A" && !event.target.hasAttribute("href")) {
                    event.preventDefault();
                    event.target.dispatchEvent(
                        new MouseEvent("click", { bubbles: true, cancelable: true })
                    );
                    return;
                }
                break;
            default:
                return;
        }

        if (nextCell) {
            focusCell(nextCell, matrix, true);
            event.preventDefault();
        }
    }

    const getNextVisibleCell = (matrix, directionX, directionY) => {
        const nextRow = Math.max(0, Math.min((matrix.rowIndex + directionY), matrix.cells.length - 1));
        const nextCol = Math.max(0, Math.min((matrix.colIndex + directionX), matrix.cells[nextRow].length - 1));
        if (nextRow === matrix.rowIndex && nextCol === matrix.colIndex) return null;
        return matrix.cells[nextRow][nextCol];
    };

    const getMatrixInfo = () => {
        const {rowIndex, colIndex} = currentPosRef.current;
        const rows = Array.from(baseRef.current?.querySelectorAll(rowSelector));
        const cells = rows.map(row => {
            const matches = Array.from(row.querySelectorAll(cellSelector));
            return matches.filter(el =>
                !matches.some(parent => parent !== el && parent.contains(el))
            );
        });
        const currentRow = rows[rowIndex];
        const currentCell = cells[rowIndex]?.[colIndex];

        return {
            rows,
            cells,
            rowIndex,
            colIndex,
            currentCell,
            currentRow
        }
    }

    const getCellFromTarget = (target) => {
        const cell = target.closest(cellSelector);
        const withinRow = target.closest(rowSelector);
        return withinRow && cell ? cell : null;
    }

    const focusCell = (cell, matrix, updateFocus = true) => {
        const updateCurrentCell = (cell, matrix, callback) => {
            const newRowIndex = matrix.rows.findIndex(row => row.contains(cell));
            if (newRowIndex === -1) return;
            const newColIndex = matrix.cells[newRowIndex].findIndex(c => c === cell || c.contains(cell));
            if (newColIndex === -1) return;

            let newSubIndex = 0;
            const cellLeft = matrix.cells[newRowIndex][newColIndex + 1];
            if (cellLeft && cellLeft === matrix.currentCell) {
                const nextInternalComponents = cell.querySelectorAll(SELECTABLE_INTERNAL_COMPONENTS);
                newSubIndex = Math.max(0, nextInternalComponents.length - 1);
            }

            currentPosRef.current = {rowIndex: newRowIndex, colIndex: newColIndex, subIndex: newSubIndex};

            if (onFocusCellChange && typeof onFocusCellChange === "function") {
                updatingRef.current = true;
                onFocusCellChange(currentPosRef.current);
                requestAnimationFrame(() => {
                    updatingRef.current = false;
                });
            }

            requestAnimationFrame(()=> {
                if (callback && typeof callback === "function") callback();
            });
        }
        const applyFocus = () => {
            const freshMatrix = getMatrixInfo();
            const subSelectionSelector = freshMatrix.currentCell.querySelectorAll(cellSelector);
            const internalComponents = freshMatrix.currentCell.querySelectorAll(SELECTABLE_INTERNAL_COMPONENTS);
            const subComponentIndex = currentPosRef.current.subIndex || 0;
            const finalTarget = subSelectionSelector.length > 0
                ? subSelectionSelector[0]
                : (internalComponents.length > 0 ? internalComponents[subComponentIndex] : freshMatrix.currentCell);

            clearTabIndex();

            finalTarget.setAttribute("tabindex", "0");

            if (finalTarget.namespaceURI === "http://www.w3.org/2000/svg") {
                finalTarget.setAttribute("focusable", "true");
            }
            // freshFinalTarget.classList.add(TAB_GROUP_FOCUS_CLASS);
            if (updateFocus) finalTarget.focus();
        }

        if (matrix.currentCell !== cell) {
            updateCurrentCell(cell, matrix, () => applyFocus());
        } else {
            applyFocus();
        }
    }

    return (
        <div ref={ref}>{children}</div>
    )
});

export default TabGroupWrap;